/**
 * Production-safe logging utility
 * In production, only errors are logged and sensitive data is filtered
 */

export enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug'
}

/**
 * Represents a log entry with all necessary metadata
 * @interface LogEntry
 */
interface LogEntry {
  /** The severity level of the log entry */
  level: LogLevel;
  /** The log message */
  message: string;
  /** Optional context data (will be sanitized) */
  context?: Record<string, any>;
  /** ISO timestamp when the log was created */
  timestamp: string;
}

/**
 * Production-safe logger that automatically sanitizes sensitive data
 * and only logs appropriate levels in production environments
 * 
 * @class Logger
 * @example
 * ```typescript
 * import { logger } from './utils/logger';
 * 
 * logger.error('Login failed', error, { userId: '123' });
 * logger.warn('Rate limit approaching', { requests: 95, limit: 100 });
 * ```
 */
class Logger {
  /** Flag indicating if we're in development mode */
  private isDevelopment = process.env.NODE_ENV === 'development';
  
  /**
   * Sanitizes error objects to prevent sensitive data leakage
   * @private
   * @param {any} error - The error to sanitize
   * @returns {any} Sanitized error object
   */
  private sanitizeError(error: any): any {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: this.isDevelopment ? error.stack : undefined
      };
    }
    return error;
  }
  
  /**
   * Recursively sanitizes context objects to remove sensitive information
   * @private
   * @param {Record<string, any>} context - The context object to sanitize
   * @returns {Record<string, any>} Sanitized context object
   */
  private sanitizeContext(context: Record<string, any> = {}): Record<string, any> {
    const sanitized = { ...context };
    
    // Remove sensitive fields
    const sensitiveKeys = ['password', 'token', 'authorization', 'secret', 'key', 'jwt'];
    
    const sanitizeObject = (obj: any): any => {
      if (typeof obj !== 'object' || obj === null) return obj;
      
      const result: any = Array.isArray(obj) ? [] : {};
      
      for (const key in obj) {
        const lowerKey = key.toLowerCase();
        const isSensitive = sensitiveKeys.some(sensitive => lowerKey.includes(sensitive));
        
        if (isSensitive) {
          result[key] = '[REDACTED]';
        } else if (typeof obj[key] === 'object') {
          result[key] = sanitizeObject(obj[key]);
        } else {
          result[key] = obj[key];
        }
      }
      
      return result;
    };
    
    return sanitizeObject(sanitized);
  }
  
  /**
   * Internal logging method that handles all log processing
   * @private
   * @param {LogLevel} level - The log level
   * @param {string} message - The log message
   * @param {Record<string, any>} context - Optional context data
   */
  private log(level: LogLevel, message: string, context?: Record<string, any>): void {
    const entry: LogEntry = {
      level,
      message,
      context: context ? this.sanitizeContext(context) : undefined,
      timestamp: new Date().toISOString()
    };
    
    // In production, only log errors and warnings
    if (!this.isDevelopment && level !== LogLevel.ERROR && level !== LogLevel.WARN) {
      return;
    }
    
    // Use appropriate console method
    switch (level) {
      case LogLevel.ERROR:
        console.error(`[${entry.timestamp}] ERROR:`, entry.message, entry.context);
        break;
      case LogLevel.WARN:
        console.warn(`[${entry.timestamp}] WARN:`, entry.message, entry.context);
        break;
      case LogLevel.INFO:
        if (this.isDevelopment) {
          console.info(`[${entry.timestamp}] INFO:`, entry.message, entry.context);
        }
        break;
      case LogLevel.DEBUG:
        if (this.isDevelopment) {
          console.debug(`[${entry.timestamp}] DEBUG:`, entry.message, entry.context);
        }
        break;
    }
  }
  
  /**
   * Logs error messages (always logged in production)
   * @param {string} message - The error message
   * @param {any} error - Optional error object
   * @param {Record<string, any>} context - Optional context data
   * @example logger.error('Database connection failed', dbError, { retryCount: 3 })
   */
  error(message: string, error?: any, context?: Record<string, any>): void {
    this.log(LogLevel.ERROR, message, {
      ...context,
      error: error ? this.sanitizeError(error) : undefined
    });
  }
  
  /**
   * Logs warning messages (logged in production)
   * @param {string} message - The warning message
   * @param {Record<string, any>} context - Optional context data
   * @example logger.warn('Memory usage high', { usage: '85%' })
   */
  warn(message: string, context?: Record<string, any>): void {
    this.log(LogLevel.WARN, message, context);
  }
  
  /**
   * Logs info messages (only in development)
   * @param {string} message - The info message
   * @param {Record<string, any>} context - Optional context data
   * @example logger.info('User logged in', { userId: '123' })
   */
  info(message: string, context?: Record<string, any>): void {
    this.log(LogLevel.INFO, message, context);
  }
  
  /**
   * Logs debug messages (only in development)
   * @param {string} message - The debug message
   * @param {Record<string, any>} context - Optional context data
   * @example logger.debug('Function called', { params: { id: 123 } })
   */
  debug(message: string, context?: Record<string, any>): void {
    this.log(LogLevel.DEBUG, message, context);
  }
  
  /**
   * Specialized method for logging API errors with additional metadata
   * @param {string} operation - The API operation that failed
   * @param {any} error - The error object
   * @param {Record<string, any>} context - Optional context data
   * @example logger.apiError('fetch user data', error, { userId: '123', endpoint: '/api/users' })
   */
  apiError(operation: string, error: any, context?: Record<string, any>): void {
    this.error(`API operation failed: ${operation}`, error, {
      ...context,
      operation,
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }
}

export const logger = new Logger();
export default logger;